// Angular imports
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';

// Rxjs imports
import { forkJoin, Subscription } from 'rxjs';

// 3rd party imports
import { cloneDeep } from 'lodash';
import { DxFormComponent, DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { Column } from 'devextreme/ui/data_grid';
import * as moment from 'moment-timezone';

// CC-Common imports
import { DataGridFeaturesComponent, DxWidgetUtils, IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// Component imports
import { LiteModePreferenceComponent } from '../../../shared-module/lite-mode-preference/lite-mode-preference.component';

// Service imports
import { CommunityService } from '../../../_services/data-services/community.service';
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';
import { UserService } from '../../../_services/data-services/user.service';
import { CiraHelpService } from '../../../_services/shared-services/cira-help.service';
import { HelperService } from '../../../_services/shared-services/helper.service';
import { PrecisionService } from '../../../_services/shared-services/precision.service';
import { ScreenService } from '../../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { DetailPopupsService } from '../../../cirabooks/shared-cirabooks-module/_services/detail-popups.service';
import { CBHelperService } from '../../../cirabooks/shared-cirabooks-module/_services/shared-services/cb-helper-service';
import { WorkflowSharedService } from '../../../shared-workflow-module/_services/workflow-shared.service';
import { WorkflowEditorService } from '../../_services/workflow-editor.service';
import { WorkflowSearchService } from '../../_services/workflow-search.service';
import { WorkflowService } from '../../_services/workflow.service';

// Model imports
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';
import { PropertyCommunityInfoParameterDto } from '../../../_models/property-community-info-parameter-dto';
import { PropertyCommunityInfoResultDto } from '../../../_models/property-community-info-result-dto';
import { ActionMenuEvent, ActionMenuInfo } from '../../../shared-module/_models/action-menu-info';
import { WFESpecialHandlingResultDto } from '../../../shared-workflow-module/_models/wfe-special-handling-result-dto';
import { WorkFlowParametersDto } from '../../../shared-workflow-module/_models/workflow-parameters-dto';
import { MultiUserNameDisplayInfo } from '../../_models/common/multi-user-name-display-info';
import { WFSearchParameterDto } from '../../_models/workflow-search/wf-search-parameter-dto';
import { WFSVendorSelectorParameterDto } from '../../_models/workflow-search/wfs-vendor-selector-parameter-dto';
import { WFSVendorSelectorResultDto } from '../../_models/workflow-search/wfs-vendor-selector-result-dto';
import { WorkflowCommunityAttributeParameterDto } from '../../_models/workflow-search/workflow-community-attribute-parameter-dto';
import { WorkflowSearchResultDto } from '../../_models/workflow-search/workflow-search-result-dto';
import { WFSpecialHandlingParametersDto } from '../../_models/workflow-search/wf-special-handling-parameters-dto';
import { WFReverseAPBillParametersDto } from '../../_models/workflow-search/wf-reverse-ap-bill-parameters-dto';

// Constant imports
import { CiraHelpKeyConstants } from '../../../_constants/cira-help-key-constants';
import { MessageInterval } from '../../../_constants/messages-intervals';
import { StringFormatConstants } from '../../../_constants/string-format-constants';
import { WorkflowConstants } from '../../../shared-workflow-module/_constants/worflow-constants';
import { WorkflowActionConstants } from '../../_constants/workflow-action-constants';

@Component({
    selector: 'app-workflow-search',
    templateUrl: './workflow-search.component.html',
    styleUrls: ['./workflow-search.component.scss']
})
export class WorkflowSearchComponent implements OnInit, OnDestroy {
    @ViewChild('workflowEditorPopup', { static: false }) workflowEditorPopup: DxPopupComponent;
    @ViewChild('invoiceForm', { static: false }) invoiceForm: DxFormComponent;
    @ViewChild('liteModePreference', { static: false }) liteModePreference: LiteModePreferenceComponent;
    @ViewChild('searchMessageBox', { static: false }) searchMessageBox: MessageBoxComponent;
    @ViewChild('invoiceGrid', { static: false }) invoiceGrid: DataGridFeaturesComponent;

    originalCommunitylist: CommunitySelectorResultDto[] = [];
    communityList: CommunitySelectorResultDto[] = [];
    communityColumns: any[];
    vendorColumns: Array<Column>;
    communityGridBoxValue: number[];
    vendorGridBoxValue: number[] = [];
    selectedCommunity: CommunitySelectorResultDto;
    managementCompanyId: number;

    vendorList: WFSVendorSelectorResultDto[] = [];
    terminatedOptions: any[] = [];
    vendorStatusOptions: any[] = [];
    locations: any[] = [];
    originalLocations: any[];
    managementCompanyList: any[] = [];
    viewList: any[] = [];
    statusOptions: string[] = [];

    searchResults: WorkflowSearchResultDto[] = [];

    isSmallScreen: boolean;
    currencyFormat: string;

    workflowEditorTitle: string;
    selectedWorkflow: WorkflowSearchResultDto;
    selectedVendorStatusOption: boolean;

    showWorkflowEditor = false;
    showWorkFlowNotesPopUp = false;
    showWorkFlowBoardNotesPopUp = false;
    showWorkFlowHistoryPopUp = false;
    showWorkFlowBoardHistoryPopUp = false;
    showWorkflowViewer = false;
    showPaymentHistory = false;

    showReverseAPBillPopUp = false;
    reverseAPBillPopUpTitle: string;
    reversalDate: Date;

    showchangeSpecialHandlingPopUp = false;
    changeSpecialHandlingPopUpTitle: string;
    specialInstruction: string;

    specialHandlingList: WFESpecialHandlingResultDto[] = [];
    selectedSpecialHandlingId: number;

    loadingVisible = false;
    isInternalUser = false;
    isPopupMode = false;
    wfPopupParams = null;

    workflowSearchParameterDto: WFSearchParameterDto;
    selectedContext: PropertyCommunityInfoResultDto;

    actionMenuItems: Array<ActionMenuInfo> = [];

    dateFormat = StringFormatConstants.dateFormat;
    iconNotes: string = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes: string = IconConstants.NOTES_EMPTY;
    iconPDF: string = IconConstants.PDF_FILE_GRID;
    iconHistory: string = IconConstants.HISTORY;
    iconCancel: string = IconConstants.CANCEL;
    iconCheck: string = IconConstants.CHECK;
    iconRefresh: string = IconConstants.REFRESH;
    iconLock: string = IconConstants.LOCK;

    contextManagerSubscription: Subscription;
    helpLink: string;

    disableLocationSelection = false;
    disableManagementCompanySelection = false;

    uniqueResponsibleUsers: Array<any>;

    isCorporateCirabook = false;

    showWorkOrder = false;
    linkedWorkOrderIds: Array<number> = [];

    forceReloadSubscription: Subscription;
    detailsPopupSubscription: Subscription;

    selectedWorkOrderId: number;

    selectedUserConfig: MultiUserNameDisplayInfo;
    showUsersListPopup = false;

    showCommunityAttribution = false;
    showFullFilters = true;
    showLimitedFilters = false;
    isCommunityChanged: boolean;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly screenService: ScreenService,
        private readonly communityService: CommunityService,
        private readonly errorService: ServerErrorService,
        private readonly contextManagerService: ContextManagerService,
        private readonly helperService: HelperService,
        private readonly workflowService: WorkflowService,
        private readonly workflowSearchService: WorkflowSearchService,
        private readonly cbHelperService: CBHelperService,
        private readonly userService: UserService,
        private readonly precisionService: PrecisionService,
        private readonly ciraHelpService: CiraHelpService,
        private readonly workflowSharedService: WorkflowSharedService,
        private readonly titleService: Title,
        private readonly detailPopupsService: DetailPopupsService,
        private readonly workflowEditorService: WorkflowEditorService
    ) {
        this.helpLink = this.ciraHelpService.getHelpLinkByKey(CiraHelpKeyConstants.cbAPSearchInvoices);
        this.currencyFormat = this.precisionService.updatePrecisionPoint(StringFormatConstants.currencyFormat);
        this.selectedCommunity = new CommunitySelectorResultDto();
        this.isInternalUser = this.userService.isInternal();
        this.communityColumns = this.communityService.getCommunitySelectorColumns();

        this.route.queryParamMap.subscribe((params) => {
            if (params.get('uuid')) {
                const uuid = params.get('uuid');
                if (Utils.notNullOrEmpty(uuid)) {
                    const filters = localStorage.getItem(uuid);
                    if (Utils.notNullOrEmpty(filters)) {
                        const searchParams = JSON.parse(filters);
                        this.wfPopupParams = searchParams;
                        this.getSelectedContext();
                        localStorage.removeItem(uuid);
                    }
                }
            } else {
                if (Utils.notNullOrEmpty(params.keys)) {
                    this.isPopupMode = Utils.notNullOrEmpty(params.get('mode'));

                    this.wfPopupParams = {};
                    params.keys.forEach((key) => {
                        this.wfPopupParams[key] = params.get(key);
                    });

                    this.showFullFilters = Utils.notNullOrEmpty(this.wfPopupParams.showFullFilters)
                        ? Utils.equalsIgnoreCase(this.wfPopupParams.showFullFilters, 'true')
                        : false;
                    if (Utils.isTrue(this.showFullFilters)) {
                        this.showLimitedFilters = false;
                    } else {
                        this.showLimitedFilters = Utils.notNullOrEmpty(this.wfPopupParams.showLimitedFilters)
                            ? Utils.equalsIgnoreCase(this.wfPopupParams.showLimitedFilters, 'true')
                            : false;
                    }
                }
            }
        });
    }

    ngOnInit() {
        this.configureColumns();
        this.titleService.setTitle('Search Invoices');
        this.updateScreenSize();
        this.screenService.changed.subscribe(this.updateScreenSize.bind(this));

        this.onFilterFormReady();
        this.cbHelperService.getSubjectFilterFormReady().subscribe(this.onFilterFormReady.bind(this));

        this.workflowSearchParameterDto = new WFSearchParameterDto();
        this.resetWorkflowSearchFields();

        this.configureDefaults();

        this.getSelectedContext();
        this.contextManagerSubscription = this.contextManagerService.getContext().subscribe(() => {
            this.getSelectedContext();
        });

        this.detailsPopupSubscription = this.workflowSharedService.getDetailsParams().subscribe((params: any) => {
            const paymentId = Utils.notNullAndDefined(params.paymentId) ? parseInt(params.paymentId) : null;
            const communityId = Utils.notNullAndDefined(params.communityId) ? parseInt(params.communityId) : null;
            const managementCompanyId = Utils.notNullAndDefined(params.managementCompanyId)
                ? parseInt(params.managementCompanyId)
                : null;

            if (
                Utils.notNullAndDefined(paymentId) &&
                Utils.notNullAndDefined(communityId) &&
                Utils.notNullAndDefined(managementCompanyId)
            ) {
                this.detailPopupsService.updateSubjectAPPaymentDetail(managementCompanyId, communityId, paymentId);
            }
        });
    }

    ngOnDestroy() {
        if (Utils.notNullAndDefined(this.detailsPopupSubscription)) {
            this.detailsPopupSubscription.unsubscribe();
        }
    }

    onFilterFormReady(): void {
        const filterForm = this.cbHelperService.getFilterForm();
        if (Utils.notNullOrEmpty(filterForm) && Utils.notNullOrEmpty(filterForm.instance)) {
            filterForm.instance.option('items', []);
        }
    }

    private configureColumns(): void {
        this.vendorColumns = [
            {
                dataField: 'vendorName',
                dataType: 'string',
                allowSearch: true,
                allowFiltering: false
            },
            {
                dataField: 'active',
                dataType: 'boolean',
                visible: Utils.isFalse(this.selectedVendorStatusOption),
                cellTemplate: 'trueValueCellTemplate',
                allowFiltering: false,
                allowSearch: false,
                width: 40,
                alignment: 'center'
            }
        ];
    }

    onCommunitySelection(event): void {
        this.searchResults = [];
        this.isCommunityChanged = true;
        if (Utils.notNullOrEmpty(event) && Utils.notNullOrEmpty(event.selectedRow)) {
            if (
                Utils.isNullOrUndefined(this.selectedCommunity) ||
                Utils.notEquals(this.selectedCommunity.communityId, event.selectedRow[0].communityId)
            ) {
                this.resetVendor();
                this.selectedCommunity = event.selectedRow[0];

                this.workflowSearchParameterDto.communityId = this.selectedCommunity.communityId;
                this.workflowSearchParameterDto.locationId = this.selectedCommunity.locationId;
                this.workflowSearchParameterDto.managementCompanyId = this.selectedCommunity.managementCompanyId;
                this.disableLocationSelection = true;
                this.disableManagementCompanySelection = true;

                // search vendor list and default begin date
                this.populateDependentList(false);
            }
        } else {
            this.selectedCommunity = null;
            this.workflowSearchParameterDto.communityId = null;
            this.isCommunityChanged = false;
            this.workflowSearchParameterDto.locationId = null;
            this.disableLocationSelection = false;
            this.workflowSearchParameterDto.managementCompanyId = null;
            this.disableManagementCompanySelection = false;
            if (
                Utils.notNullOrEmpty(this.workflowSearchParameterDto.locationId) ||
                Utils.notNullOrEmpty(this.workflowSearchParameterDto.managementCompanyId)
            ) {
                this.resetVendor();
                // search vendor list
                this.populateDependentList(false);
            } else {
                this.vendorList = [];
            }
        }
    }

    onVendorSelection(selectedVendorObject: any): void {
        if (Utils.notNullAndDefined(selectedVendorObject) && Utils.notNullOrEmpty(selectedVendorObject.selectedRow)) {
            if (Utils.notEquals(this.vendorGridBoxValue[0], selectedVendorObject.selectedRow[0].vendorId)) {
                this.vendorGridBoxValue = [selectedVendorObject.selectedRow[0].vendorId];
                this.workflowSearchParameterDto.vendorId = selectedVendorObject.selectedRow[0].vendorId;
            }
        } else {
            this.resetVendor();
        }
    }

    onLocationChange() {
        this.searchResults = [];

        this.resetVendor();

        if (Utils.isNullOrEmpty(this.workflowSearchParameterDto.locationId)) {
            // community is already null, hence clearing only parent level.
            this.workflowSearchParameterDto.managementCompanyId = null;
        } else {
            // select management id based on selected location
            const selectedLocation = this.originalLocations.find((loc) =>
                Utils.equals(loc.id, this.workflowSearchParameterDto.locationId)
            );

            if (Utils.notNullAndDefined(selectedLocation)) {
                this.workflowSearchParameterDto.managementCompanyId = selectedLocation.managementCompanyId;
            }
        }

        this.resetCommunityLocationSelector(this.workflowSearchParameterDto.managementCompanyId);

        // To enable if location is null or empty
        this.disableManagementCompanySelection = Utils.notNullOrEmpty(this.workflowSearchParameterDto.locationId);

        if (this.workflowSearchParameterDto.locationId || this.workflowSearchParameterDto.managementCompanyId) {
            // search vendor list
            this.populateDependentList(false);
        } else {
            this.vendorList = [];
        }
    }

    onManagementCompanyChange(): void {
        this.searchResults = [];
        if (Utils.isTrue(this.isCommunityChanged)) {
            this.isCommunityChanged = false;
        }

        this.resetCommunityLocationSelector(this.workflowSearchParameterDto.managementCompanyId);

        this.resetVendor();

        if (Utils.notNullOrEmpty(this.workflowSearchParameterDto.managementCompanyId)) {
            this.populateDependentList(false);
        } else {
            this.vendorList = [];
        }
    }

    onStatusChange(): void {
        this.searchResults = [];
        this.communityGridBoxValue =
            Utils.notNullAndDefined(this.selectedCommunity) &&
            Utils.notNullAndDefined(this.selectedCommunity.communityId)
                ? [this.selectedCommunity.communityId]
                : [];
        if (DxWidgetUtils.hasInstance(this.invoiceForm)) {
            this.invoiceForm.instance.repaint();
        }

        this.getDefaultBeginDate();
    }

    onVendorStatusChange(): void {
        this.configureColumns();
        this.searchResults = [];
        // search vendor list
        this.populateDependentList(false);
    }

    onActionItemClick(event: ActionMenuEvent<WorkflowSearchResultDto>): void {
        this.selectedWorkflow = event.record;
        const actionItem = event.item.action;
        if (Utils.equalsIgnoreCase(actionItem, WorkflowActionConstants.MANAGE_WORK_ORDERS)) {
            this.showWorkOrder = true;
            this.linkedWorkOrderIds = Utils.notNullAndDefined(this.selectedWorkflow.workOrders)
                ? this.selectedWorkflow.workOrders.split(',').map((d) => parseInt(d))
                : [];
        } else if (Utils.equalsIgnoreCase(actionItem, WorkflowActionConstants.REVERSE_AP_BILL)) {
            this.reverseAPBillPopUpTitle = 'Reverse AP Bill For AAP Workflow ' + this.selectedWorkflow.workFlowId;
            this.showReverseAPBillPopUp = true;
        } else if (Utils.equalsIgnoreCase(actionItem, WorkflowActionConstants.RETURN_TO_ACCOUNTANT)) {
            this.returnToAccountant();
        } else if (
            Utils.equalsIgnoreCase(actionItem, WorkflowActionConstants.CHANGE_TO_EFT_PAYMENT) ||
            Utils.equalsIgnoreCase(actionItem, WorkflowActionConstants.UNMARK_EFT_PAYMENT)
        ) {
            this.toggleEFTPayment();
        } else if (Utils.equalsIgnoreCase(actionItem, WorkflowActionConstants.CHANGE_SPECIAL_HANDLING)) {
            this.getSpecialHandling();
            this.changeSpecialHandlingPopUpTitle =
                'Change Special Handling For AAP Workflow #' + this.selectedWorkflow.workFlowId;
            this.showchangeSpecialHandlingPopUp = true;
        } else if (Utils.equalsIgnoreCase(actionItem, WorkflowActionConstants.VIEW_DELETE_REASON)) {
            this.getViewDeleteReason();
        } else if (Utils.equalsIgnoreCase(actionItem, WorkflowActionConstants.PROCESSING)) {
            this.prepareWorkflowEditor(this.selectedWorkflow);
        } else if (Utils.equalsIgnoreCase(actionItem, WorkflowActionConstants.ATTRIBUTE_COMMUNITY)) {
            this.prepateAttributeCommunity();
        }
    }

    onLiteModeValueChange(): void {
        this.liteModePreference.show(this.workflowSearchParameterDto.liteMode);
    }

    onLiteModePreferenceChange(): void {
        this.getSearchInvoices();
    }

    setStoredLiteMode(flag: boolean): void {
        this.workflowSearchParameterDto.liteMode = flag;
    }

    onHistoryClick(record: WorkflowSearchResultDto): void {
        this.selectedWorkflow = record;
        this.showWorkFlowHistoryPopUp = true;
    }

    onWorkflowHistoryClose(): void {
        this.showWorkFlowHistoryPopUp = false;
    }

    onWorkflowBoardHistoryClose(): void {
        this.showWorkFlowBoardHistoryPopUp = false;
    }

    onNotesClick(record: WorkflowSearchResultDto): void {
        this.selectedWorkflow = record;
        this.showWorkFlowNotesPopUp = true;
    }

    onWorkflowNotesPopClose(event: boolean): void {
        this.showWorkFlowNotesPopUp = false;
        if (Utils.isTrue(event)) {
            this.getSearchInvoices();
        }
    }

    onWorkdlowBoardNotesPopClose(event: boolean): void {
        this.showWorkFlowBoardNotesPopUp = false;
        if (Utils.isTrue(event)) {
            this.getSearchInvoices();
        }
    }

    onBoardHistoryClick(record: WorkflowSearchResultDto): void {
        this.selectedWorkflow = record;
        this.showWorkFlowBoardHistoryPopUp = true;
    }

    onBoardNotesClick(record: WorkflowSearchResultDto): void {
        this.selectedWorkflow = record;
        this.showWorkFlowBoardNotesPopUp = true;
    }

    onDownloadFile(record: WorkflowSearchResultDto): void {
        const workFlowParams = new WorkFlowParametersDto();
        workFlowParams.workflowId = record.workFlowId;
        workFlowParams.communityId = record.communityId;

        this.downloadInvoice(workFlowParams);
    }

    onWorkflowIdCellClick(record: WorkflowSearchResultDto): void {
        this.selectedWorkflow = record;
        if (Utils.notNullAndDefined(this.selectedWorkflow.communityId)) {
            this.prepareWorkflowEditor(record);
        } else {
            this.prepateAttributeCommunity();
        }
    }

    onWorkflowClose(event: boolean): void {
        this.showWorkflowEditor = false;
        this.workflowEditorPopup.visible = false;
        if (Utils.isTrue(event)) {
            this.getSearchInvoices();
        }
    }

    onSearchRefreshClick(): void {
        this.getSearchInvoices();
    }

    getManagementCompanyHint(): string {
        let defaultManagementCompany = { displayName: 'Select Management Company' };
        if (this.workflowSearchParameterDto.managementCompanyId && Utils.notNullOrEmpty(this.managementCompanyList)) {
            const managementCompany = this.managementCompanyList.find((d) =>
                Utils.equals(d.id, this.workflowSearchParameterDto.managementCompanyId)
            );
            defaultManagementCompany = managementCompany || defaultManagementCompany;
        }
        return defaultManagementCompany.displayName;
    }

    getLocationHint(): string {
        let defaultLoc = { displayName: 'Select Location' };
        if (this.workflowSearchParameterDto.locationId && Utils.notNullOrEmpty(this.locations)) {
            const loc = this.locations.find((d) => Utils.equals(d.id, this.workflowSearchParameterDto.locationId));
            defaultLoc = loc || defaultLoc;
        }
        return defaultLoc.displayName;
    }

    onSpecialHandlingOkClick(): void {
        this.saveChangeInSpecialHandling();
    }

    onReverseAPBillOkClick(): void {
        this.updateReverseAPBill();
    }

    openPageInWindow(): void {
        const pageUrl = window.location.href.split('?')[0];
        const uuid = Utils.getUUID();
        localStorage.setItem(uuid, JSON.stringify(this.workflowSearchParameterDto));
        window.open(`${pageUrl}?uuid=${uuid}`, '_blank').focus();
    }

    onWorkflowViewerClose(): void {
        this.showWorkflowViewer = false;
    }

    isActionItemVisible(item: ActionMenuInfo, record: WorkflowSearchResultDto): boolean {
        return (
            (Utils.equalsIgnoreCase(item.action, WorkflowActionConstants.PROCESSING) &&
                Utils.isTrue(record.allowProcessing)) ||
            (Utils.equalsIgnoreCase(item.action, WorkflowActionConstants.MANAGE_WORK_ORDERS) &&
                Utils.isTrue(record.allowManageWorkOrders)) ||
            (Utils.equalsIgnoreCase(item.action, WorkflowActionConstants.REVERSE_AP_BILL) &&
                Utils.isTrue(record.allowReverseAPBill) &&
                Utils.equalsIgnoreCase(record.workflowStatus, WorkflowConstants.APPROVED)) ||
            (Utils.equalsIgnoreCase(item.action, WorkflowActionConstants.RETURN_TO_ACCOUNTANT) &&
                Utils.isTrue(record.allowReturnToAccountant) &&
                Utils.equalsIgnoreCase(record.workflowStatus, WorkflowConstants.APPROVED)) ||
            (Utils.equalsIgnoreCase(item.action, WorkflowActionConstants.CHANGE_TO_EFT_PAYMENT) &&
                Utils.isTrue(record.allowToggleEFTPayment) &&
                Utils.equalsIgnoreCase(record.workflowStatus, WorkflowConstants.APPROVED) &&
                Utils.isFalse(record.eft)) ||
            (Utils.equalsIgnoreCase(item.action, WorkflowActionConstants.UNMARK_EFT_PAYMENT) &&
                Utils.isTrue(record.allowToggleEFTPayment) &&
                Utils.equalsIgnoreCase(record.workflowStatus, WorkflowConstants.APPROVED) &&
                Utils.isTrue(record.eft)) ||
            (Utils.equalsIgnoreCase(item.action, WorkflowActionConstants.CHANGE_SPECIAL_HANDLING) &&
                Utils.isTrue(record.allowChangeSpecialHandling) &&
                Utils.equalsIgnoreCase(record.workflowStatus, WorkflowConstants.APPROVED)) ||
            (Utils.equalsIgnoreCase(item.action, WorkflowActionConstants.VIEW_DELETE_REASON) &&
                Utils.equalsIgnoreCase(record.invoiceStatus, WorkflowConstants.DELETE_STATUS)) ||
            (Utils.equalsIgnoreCase(item.action, WorkflowActionConstants.ATTRIBUTE_COMMUNITY) &&
                Utils.isTrue(record.allowAttributeCommunity))
        );
    }

    hasAction(record: WorkflowSearchResultDto): boolean {
        return (
            Utils.isTrue(record.allowManageWorkOrders) ||
            Utils.isTrue(record.allowReturnToAccountant) ||
            Utils.isTrue(record.allowInvoiceApproval) ||
            Utils.isTrue(record.allowReverseAPBill) ||
            Utils.isTrue(record.allowToggleEFTPayment) ||
            Utils.isTrue(record.allowChangeSpecialHandling) ||
            Utils.isTrue(record.allowProcessing) ||
            Utils.isTrue(record.allowAttributeCommunity)
        );
    }

    onPaidPaymentStatusClick(record: WorkflowSearchResultDto): void {
        this.selectedWorkflow = record;
        const selectedCommunity = this.communityList.find((d) => Utils.equals(d.communityId, record.communityId));
        if (Utils.notNullAndDefined(selectedCommunity)) {
            this.managementCompanyId = selectedCommunity.managementCompanyId;
        }
        this.showPaymentHistory = true;
    }

    onPaymentHistoryClose(forceReload: boolean): void {
        this.showPaymentHistory = false;

        if (Utils.isTrue(forceReload)) {
            this.getSearchInvoices();
        }
    }

    onWorkOrderSelectorClose(forceReload: boolean): void {
        this.showWorkOrder = false;
        this.linkedWorkOrderIds = [];
        if (Utils.isTrue(forceReload)) {
            this.getSearchInvoices();
        }
    }

    onWorkOrderClick(workOrderId: string, record: WorkflowSearchResultDto): void {
        const url = `my-work/workorder?communityId=${record.communityId}&workOrderId=${workOrderId}`;
        this.helperService.openWindow(url, '_blank', null, null);
    }

    showUsersList(event: WorkflowSearchResultDto): void {
        this.selectedUserConfig = new MultiUserNameDisplayInfo();
        this.selectedUserConfig.users = event.dashboardResponsibleUsers;
        this.selectedUserConfig.communityId = event.communityId;
        this.selectedUserConfig.title = 'Dashboard Responsible Users';
        this.showUsersListPopup = true;
    }

    onUserListClose(): void {
        this.showUsersListPopup = false;
        this.selectedUserConfig = null;
    }

    onCommunityAttributionClose(forceRefresh: boolean): void {
        this.showCommunityAttribution = false;
        if (Utils.isTrue(forceRefresh)) {
            this.getSearchInvoices();
        }
    }

    onCommunityAttributionSave(communityId: number): void {
        if (Utils.notNullAndDefined(communityId)) {
            const baseParam: WorkflowCommunityAttributeParameterDto = new WorkflowCommunityAttributeParameterDto();
            baseParam.workflowId = this.selectedWorkflow.workFlowId;
            baseParam.communityId = communityId;

            this.workflowEditorService.attributeCommunity(baseParam).subscribe(
                () => {
                    notify('Community attribution successful', 'success', MessageInterval.successMsgInterval);

                    /* As discussed with Sandeep, on DEV-3952 */
                    // setTimeout(() => {
                    //     this.selectedWorkflow.communityId = communityId;
                    //     this.prepareWorkflowEditor(this.selectedWorkflow);
                    // }, 500);
                },
                (error) => {
                    this.errorService.displayError(error);
                }
            );
        } else {
            this.selectedWorkflow = null;
        }
    }

    onVendorNameClick(data: WorkflowSearchResultDto): void {
        if (Utils.notNullAndDefined(data)) {
            const contextType = 'Community';
            const contextId = data.communityId;
            const payeeCode = data.payeeCode;
            const queryString = `contextType=${encodeURIComponent(
                contextType
            )}&contextId=${contextId}&vendorCode=${encodeURIComponent(payeeCode)}`;
            this.helperService.openDefaultVendorManagementPageInNewWindow(queryString);
        }
    }

    private resetVendor(): void {
        this.workflowSearchParameterDto.vendorId = null;
        this.vendorGridBoxValue = [];
    }

    private resetCommunityLocationSelector(managementCompanyId: number): void {
        // Note: To filter location list based on management company selection.
        if (Utils.isNullOrEmpty(managementCompanyId)) {
            if (
                Utils.notEquals(this.originalLocations.length, this.locations.length) &&
                Utils.isNullOrEmpty(this.workflowSearchParameterDto.locationId)
            ) {
                // Adding setTimeout to add delay for location select-box to handle expression change error on resetting everything
                setTimeout(() => {
                    this.locations = this.originalLocations;
                }, 200);
            }
            if (
                Utils.notEquals(this.originalCommunitylist.length, this.communityList.length) &&
                Utils.isNullOrEmpty(this.workflowSearchParameterDto.communityId)
            ) {
                // Adding setTimeout to add delay for community multi-select-column-selector to handle expression change error on resetting everything
                setTimeout(() => {
                    this.communityList = this.originalCommunitylist;
                }, 200);
            }
        } else {
            if (Utils.isNullOrEmpty(this.workflowSearchParameterDto.locationId)) {
                this.locations = this.originalLocations.filter((location) =>
                    Utils.equals(location.managementCompanyId, managementCompanyId)
                );
            }
            if (Utils.isNullOrEmpty(this.workflowSearchParameterDto.communityId)) {
                if (Utils.notNullOrEmpty(this.workflowSearchParameterDto.locationId)) {
                    this.communityList = this.originalCommunitylist.filter(
                        (community) =>
                            Utils.equals(community.managementCompanyId, managementCompanyId) &&
                            Utils.equals(community.locationId, this.workflowSearchParameterDto.locationId)
                    );
                } else {
                    if (Utils.notNullOrEmpty(managementCompanyId)) {
                        this.communityList = this.originalCommunitylist.filter((community) =>
                            Utils.equals(community.managementCompanyId, managementCompanyId)
                        );
                    }
                }
            }
        }
    }

    private getCommunityLocationManagementCompanyList(includeInactive = false) {
        this.communityService.getCommunities(true, includeInactive).subscribe(
            (data) => {
                this.originalCommunitylist = cloneDeep(data);
                this.communityList = data;

                const allLocations = data.map((d) => ({
                    displayName: d.locationName,
                    id: d.locationId,
                    managementCompanyId: d.managementCompanyId
                }));
                const locations = [];
                allLocations.forEach((loc) => {
                    if (!locations.find((d) => Utils.equals(d.id, loc.id))) {
                        locations.push(loc);
                    }
                });
                this.locations = locations;
                this.originalLocations = cloneDeep(this.locations);

                const managementCompanyList = data.map((d) => {
                    return Object.assign([], {
                        displayName: d.managementCompanyName,
                        id: d.managementCompanyId
                    });
                });
                const managementCompanies = [];
                managementCompanyList.forEach((mc) => {
                    if (!managementCompanies.find((d) => Utils.equals(d.id, mc.id))) {
                        managementCompanies.push(mc);
                    }
                });
                this.managementCompanyList = managementCompanies;

                if (Utils.notNullOrEmpty(this.wfPopupParams)) {
                    const communityId = parseInt(this.wfPopupParams.communityId);
                    if (Utils.notNullOrEmpty(this.wfPopupParams.locationId)) {
                        this.workflowSearchParameterDto.locationId = parseInt(this.wfPopupParams.locationId);
                    }
                    if (Utils.notNullOrEmpty(this.wfPopupParams.managementCompanyId)) {
                        this.workflowSearchParameterDto.managementCompanyId = parseInt(
                            this.wfPopupParams.managementCompanyId
                        );
                    }
                    if (Utils.notNullOrEmpty(this.wfPopupParams.sourceType)) {
                        this.workflowSearchParameterDto.sourceType = this.wfPopupParams.sourceType;
                    }
                    if (Utils.notNullAndDefined(this.wfPopupParams.vendorId)) {
                        this.workflowSearchParameterDto.vendorId = this.wfPopupParams.vendorId;
                    }
                    if (Utils.notNullOrEmpty(this.wfPopupParams.fromDate)) {
                        this.workflowSearchParameterDto.fromDate = this.wfPopupParams.fromDate;
                    }

                    this.selectedCommunity = this.communityList.find((d) => Utils.equals(d.communityId, communityId));
                    if (Utils.notNullOrEmpty(this.selectedCommunity)) {
                        this.communityGridBoxValue = Utils.notNullOrEmpty(this.selectedCommunity)
                            ? [this.selectedCommunity.communityId]
                            : [];
                        this.workflowSearchParameterDto.communityId = communityId;

                        this.workflowSearchParameterDto.workflowId = Utils.notNullAndDefined(
                            this.wfPopupParams.workflowId
                        )
                            ? this.wfPopupParams.workflowId
                            : null;
                        if (Utils.notNullOrEmpty(this.wfPopupParams.vendorId)) {
                            this.workflowSearchParameterDto.vendorId = parseInt(this.wfPopupParams.vendorId);
                            this.populateDependentList(false);
                        } else {
                            this.onSearchRefreshClick();
                        }
                    } else if (Utils.notNullOrEmpty(this.wfPopupParams.workflowId)) {
                        this.workflowSearchParameterDto.workflowId = parseInt(this.wfPopupParams.workflowId);
                        this.onSearchRefreshClick();
                    } else if (Utils.notNullOrEmpty(this.wfPopupParams.fromDate)) {
                        this.getSearchInvoices();
                    } else {
                        if (Utils.isFalse(includeInactive)) {
                            this.getCommunityLocationManagementCompanyList(true);
                        }
                    }
                }
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private getDefaultBeginDate(clearCache = false): void {
        if (this.workflowSearchParameterDto.sourceType !== 'Open') {
            if (
                Utils.notNullAndDefined(this.selectedCommunity) &&
                Utils.notNullAndDefined(this.selectedCommunity.communityId)
            ) {
                const propertCommunityInfoParams: PropertyCommunityInfoParameterDto =
                    new PropertyCommunityInfoParameterDto();
                propertCommunityInfoParams.communityId = this.selectedCommunity.communityId;
                propertCommunityInfoParams.clearCache = clearCache;

                this.loadingVisible = true;

                this.workflowSearchService.getSearchDefaultBeginDate(propertCommunityInfoParams).subscribe(
                    (result: Date) => {
                        this.loadingVisible = false;
                        this.workflowSearchParameterDto.fromDate = result;
                    },
                    (error) => {
                        this.loadingVisible = false;
                        this.errorService.displayError(error);
                    }
                );
            } else {
                if (Utils.isNullOrUndefined(this.workflowSearchParameterDto.fromDate)) {
                    const invoiceDate = moment().subtract(6, 'months');
                    this.workflowSearchParameterDto.fromDate = moment(invoiceDate, 'MM/DD/YYYY').toDate();
                }
            }
        } else {
            this.workflowSearchParameterDto.fromDate = null;
        }
    }

    private populateDependentList(clearCache: boolean): void {
        const apiCalls: Array<any> = [];
        const vendorSelectorParam: WFSVendorSelectorParameterDto = new WFSVendorSelectorParameterDto();
        if (Utils.notNullOrEmpty(this.selectedCommunity) && !!this.selectedCommunity.communityId) {
            vendorSelectorParam.communityId = this.selectedCommunity.communityId;
        } else if (this.workflowSearchParameterDto.locationId) {
            vendorSelectorParam.locationId = this.workflowSearchParameterDto.locationId;
        } else if (this.workflowSearchParameterDto.managementCompanyId) {
            vendorSelectorParam.managementCompanyId = this.workflowSearchParameterDto.managementCompanyId;
        }
        if (
            !!vendorSelectorParam.communityId ||
            !!vendorSelectorParam.locationId ||
            !!vendorSelectorParam.managementCompanyId
        ) {
            vendorSelectorParam.activeVendorsOnly = this.selectedVendorStatusOption;
            vendorSelectorParam.clearCache = clearCache;
            // search vendor list
            apiCalls.push({
                key: 'vendor',
                api: this.workflowSearchService.getSearchVendors(vendorSelectorParam)
            });
        }

        if (this.workflowSearchParameterDto.sourceType !== 'Open') {
            if (Utils.notNullOrEmpty(this.selectedCommunity) && !!this.selectedCommunity.communityId) {
                const baseParams: PropertyCommunityInfoParameterDto = new PropertyCommunityInfoParameterDto();
                baseParams.communityId = this.selectedCommunity.communityId;
                baseParams.clearCache = clearCache;

                // populate default begin date
                apiCalls.push({
                    key: 'defaultBeginDate',
                    api: this.workflowSearchService.getSearchDefaultBeginDate(baseParams)
                });
            } else {
                const startDayOfMonth = moment().startOf('month');
                const invoiceDate = startDayOfMonth.subtract(2, 'years');
                this.workflowSearchParameterDto.fromDate = moment(invoiceDate, 'MM/DD/YYYY').toDate();
            }
        } else {
            this.workflowSearchParameterDto.fromDate = null;
        }

        if (Utils.notNullOrEmpty(apiCalls)) {
            this.loadingVisible = true;
            const apis = apiCalls.map((d) => d.api);
            forkJoin(apis).subscribe((results) => {
                this.loadingVisible = false;
                let vendorIndex = -1;
                let defaultBeginDateIndex = -1;
                apiCalls.forEach((api, i) => {
                    if (Utils.equalsIgnoreCase(api.key, 'vendor')) {
                        vendorIndex = i;
                    } else if (Utils.equalsIgnoreCase(api.key, 'defaultBeginDate')) {
                        defaultBeginDateIndex = i;
                    }
                });

                if (defaultBeginDateIndex > -1) {
                    this.workflowSearchParameterDto.fromDate = results[defaultBeginDateIndex];
                }

                if (vendorIndex > -1) {
                    const vendorResults: WFSVendorSelectorResultDto[] = results[vendorIndex];
                    vendorResults.sort((a, b) => (a.vendorName < b.vendorName ? -1 : 1));
                    this.workflowSearchParameterDto.vendorId =
                        Utils.notNullAndDefined(this.wfPopupParams) &&
                        Utils.notNullAndDefined(this.wfPopupParams.vendorId)
                            ? parseInt(this.wfPopupParams.vendorId)
                            : this.workflowSearchParameterDto.vendorId;
                    this.vendorList = vendorResults;
                    if (Utils.notNullAndDefined(this.workflowSearchParameterDto.vendorId)) {
                        this.onSearchRefreshClick();
                    }
                }
            });
        }
    }

    private getSpecialHandling(): void {
        const workFlowParams: WorkFlowParametersDto = new WorkFlowParametersDto();
        workFlowParams.workflowId = this.selectedWorkflow.workFlowId;
        workFlowParams.communityId = this.selectedWorkflow.communityId;

        this.workflowService.getSpecialHandling(workFlowParams).subscribe(
            (result: WFESpecialHandlingResultDto[]) => {
                this.specialHandlingList = result;
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private getSearchInvoices(): void {
        this.workflowSearchParameterDto.communityId =
            Utils.notNullOrEmpty(this.selectedCommunity) && this.selectedCommunity.communityId
                ? this.selectedCommunity.communityId
                : null;

        this.loadingVisible = true;
        this.workflowSearchService.getSearchInvoices(this.workflowSearchParameterDto).subscribe(
            (result: WorkflowSearchResultDto[]) => {
                this.searchResults = result;
                this.uniqueResponsibleUsers = this.helperService.prepareUniqueResponsibleUsers(
                    this.searchResults,
                    'dashboardResponsibleUsers'
                );

                if (Utils.notNullAndDefined(this.invoiceGrid)) {
                    this.invoiceGrid.repaint();
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private downloadInvoice(params: WorkFlowParametersDto): void {
        this.loadingVisible = true;
        this.workflowSharedService.downloadInvoice(params).subscribe(
            (result: Blob) => {
                const fileURL: string = this.helperService.getFileUrlFromBlob(result);
                window.open(
                    fileURL,
                    '_blank',
                    'noopener,noreferrer,scrollbars=yes,status=yes,resizable=yes,menubar=yes,toolbar=yes,titlebar=yes'
                );
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private saveChangeInSpecialHandling(): void {
        const specialHandlingParams: WFSpecialHandlingParametersDto = new WFSpecialHandlingParametersDto();
        specialHandlingParams.specialHandlingId = this.selectedSpecialHandlingId;
        specialHandlingParams.communityId = this.selectedWorkflow.communityId;
        specialHandlingParams.workflowId = this.selectedWorkflow.workFlowId;
        specialHandlingParams.specialInstruction = this.specialInstruction;

        this.loadingVisible = true;
        this.workflowService.updateSpecialHandling(specialHandlingParams).subscribe(
            (result: string) => {
                this.notifyAndReloadSearchInvoices(result);
                this.loadingVisible = false;
                this.showchangeSpecialHandlingPopUp = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private toggleEFTPayment(): void {
        const baseParam: WorkFlowParametersDto = new WorkFlowParametersDto();
        baseParam.workflowId = this.selectedWorkflow.workFlowId;
        baseParam.communityId = this.selectedWorkflow.communityId;

        this.loadingVisible = true;
        this.workflowService.toggleWorkFlowPaymentMethod(baseParam).subscribe(
            (result: string) => {
                this.notifyAndReloadSearchInvoices(result);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.updateSubjectErrorMessage(error.error);
                this.loadingVisible = false;
            }
        );
    }

    private returnToAccountant(): void {
        const baseParam: WorkFlowParametersDto = new WorkFlowParametersDto();
        baseParam.workflowId = this.selectedWorkflow.workFlowId;
        baseParam.communityId = this.selectedWorkflow.communityId;

        this.loadingVisible = true;
        this.workflowService.updateToReturnToAccountant(baseParam).subscribe(
            (result: string) => {
                this.notifyAndReloadSearchInvoices(result);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private notifyAndReloadSearchInvoices(result: string): void {
        if (Utils.notNullOrEmpty(result)) {
            notify(result, 'Success', MessageInterval.successMsgInterval);
            this.getSearchInvoices();
        } else {
            notify('Error', 'Error', MessageInterval.errorMsgInterval);
        }
    }

    private updateReverseAPBill(): void {
        const baseParam: WFReverseAPBillParametersDto = new WFReverseAPBillParametersDto();
        baseParam.communityId = this.selectedWorkflow.communityId;
        baseParam.workflowId = this.selectedWorkflow.workFlowId;
        baseParam.reverseDate = this.reversalDate;

        this.loadingVisible = true;
        this.workflowService.updateReverseAPBill(baseParam).subscribe(
            (result: string) => {
                this.notifyAndReloadSearchInvoices(result);
                this.loadingVisible = false;
                this.showReverseAPBillPopUp = false;
            },
            (error) => {
                this.errorService.updateSubjectErrorMessage(error.error);
                this.loadingVisible = false;
            }
        );
    }

    private getViewDeleteReason(): void {
        const baseParam: WorkFlowParametersDto = new WorkFlowParametersDto();
        baseParam.workflowId = this.selectedWorkflow.workFlowId;
        baseParam.communityId = this.selectedWorkflow.communityId;

        this.workflowService.getViewDeleteReason(baseParam).subscribe(
            (result: string) => {
                if (Utils.notNullOrEmpty(result)) {
                    this.searchMessageBox.showInfoMessage('Info', result.toString());
                } else {
                    notify('Error', 'Error', MessageInterval.errorMsgInterval);
                }
            },
            (error) => {
                this.errorService.updateSubjectErrorMessage(error.error);
            }
        );
    }

    private prepareWorkflowEditor(record: WorkflowSearchResultDto): void {
        this.selectedWorkflow = record;
        if (Utils.isTrue(record.allowProcessing) && !Utils.startsWithIgnoreCase(record.workflowStatus, 'VE')) {
            this.showWorkflowEditor = true;
            this.workflowEditorTitle = `AAP Workflow #${this.selectedWorkflow.workFlowId}`;
            // [ANG-2510] - Data Entry Steps - Do not prepopulate community
            if (
                Utils.notNullOrEmpty(this.selectedWorkflow.community) &&
                Utils.notEqualsIgnoreCase(this.selectedWorkflow.workflowStatus, 'Scanned') &&
                Utils.notEqualsIgnoreCase(this.selectedWorkflow.workflowStatus, 'Aapverify')
            ) {
                this.workflowEditorTitle += `, Community: ${this.selectedWorkflow.community}`;
            }
        } else {
            this.showWorkflowViewer = true;
        }
    }

    private resetWorkflowSearchFields(): void {
        this.selectedCommunity = null;
        this.workflowSearchParameterDto.myWorkOnly = false;
        this.workflowSearchParameterDto.includeTerminated = false;
        this.workflowSearchParameterDto.sourceType = 'Open';
        this.workflowSearchParameterDto.liteMode = true;

        this.selectedVendorStatusOption = true;

        this.reversalDate = new Date();
    }

    private getSelectedContext(): void {
        this.selectedCommunity = this.selectedCommunity || new CommunitySelectorResultDto();
        this.selectedContext = this.contextManagerService.getSelectedContext();
        this.getCommunityLocationManagementCompanyList();

        if (Utils.notNullOrEmpty(this.selectedContext)) {
            this.selectedCommunity.communityId = this.selectedContext.communityId;
            this.selectedCommunity.communityName = this.selectedContext.communityName;
            if (Utils.isNullOrUndefined(this.workflowSearchParameterDto.locationId)) {
                this.workflowSearchParameterDto.locationId = this.selectedCommunity.locationId;
            }

            if (Utils.isNullOrUndefined(this.workflowSearchParameterDto.managementCompanyId)) {
                this.workflowSearchParameterDto.managementCompanyId = this.selectedCommunity.managementCompanyId;
            }
            // search vendor list and default begin date
            this.populateDependentList(false);
        }
    }

    private prepateAttributeCommunity(): void {
        this.showCommunityAttribution = true;
    }

    private configureDefaults(): void {
        this.selectedCommunity = null;
        this.terminatedOptions = [
            {
                displayName: 'Yes',
                value: true
            },
            {
                displayName: 'No',
                value: false
            }
        ];

        this.vendorStatusOptions = [
            {
                displayName: 'Active',
                value: true
            },
            {
                displayName: 'All',
                value: false
            }
        ];

        if (Utils.isTrue(this.isInternalUser)) {
            this.statusOptions = ['Open', 'Closed', 'Deleted', 'All'];
        } else {
            this.statusOptions = ['Open', 'Closed', 'All'];
        }

        this.viewList = [
            {
                displayName: 'Team AAP Workflow',
                value: false
            },
            {
                displayName: 'My Inbox',
                value: true
            }
        ];

        this.actionMenuItems = [
            {
                action: WorkflowActionConstants.PROCESSING,
                name: 'Process Workflow',
                separator: true,
                disabled: false,
                icon: null,
                items: []
            },
            {
                action: WorkflowActionConstants.MANAGE_WORK_ORDERS,
                name: 'Manage Work Orders',
                separator: true,
                disabled: false,
                icon: null,
                items: []
            },
            {
                action: WorkflowActionConstants.REVERSE_AP_BILL,
                name: 'Reverse AP Bill',
                disabled: false,
                separator: true,
                icon: null,
                items: []
            },
            {
                action: WorkflowActionConstants.RETURN_TO_ACCOUNTANT,
                name: 'Return To Accountant',
                disabled: false,
                separator: true,
                icon: null,
                items: []
            },
            {
                action: WorkflowActionConstants.CHANGE_TO_EFT_PAYMENT,
                name: 'Change To EFT Payment',
                disabled: false,
                separator: true,
                icon: null,
                items: []
            },
            {
                action: WorkflowActionConstants.UNMARK_EFT_PAYMENT,
                name: 'Unmark EFT Payment',
                disabled: false,
                separator: true,
                icon: null,
                items: []
            },
            {
                action: WorkflowActionConstants.CHANGE_SPECIAL_HANDLING,
                name: 'Change Special Handling',
                disabled: false,
                separator: true,
                icon: null,
                items: []
            },
            {
                action: WorkflowActionConstants.VIEW_DELETE_REASON,
                name: 'View Delete Reason',
                disabled: false,
                separator: true,
                icon: null,
                items: []
            },
            {
                action: WorkflowActionConstants.ATTRIBUTE_COMMUNITY,
                name: 'Attribute Community',
                disabled: false,
                separator: true,
                icon: null,
                items: []
            }
        ];
    }

    private updateScreenSize(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
    }
}
